import type { FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack, type Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import { useFormikContext } from 'formik'
import type { TFunction } from 'i18next'
import * as Yup from 'yup'
import { H5 } from '@dhl-ui/components'

import { RadioGroupInput } from '@dhl/inputs'
import { useNativeDialogBackdrop } from '@dhl/native-modal'
import { type Servicepoint, useServicepointSearch } from '@dhl/receiver-servicepoint'
import { ServicepointDialog, type ServicepointInputValue } from '@dhl/servicepoint-input'

import type { PreferenceFormValues } from '~/profile/model/preferences/model.js'

import { AgreedPlaceDialog } from './AgreedPlaceDialog.js'
import { DeliveryOptionRadioInput } from './DeliveryOptionRadioInput.js'
import { DisplayServicePointValue } from './DisplayServicePointValue.js'
import { DisplayAgreedPlaceValue } from './DisplayAgreedPlaceValue.js'
import { NotHomeDialog } from './NotHomeDialog.js'
import { DisplayNotHomeValue } from './DisplayNotHomeValue.js'
import type { AgreedPlaceFormValues } from './AgreedPlaceForm.js'

export type AvailableInputModes = 'not-home' | 'servicepoint' | 'agreedplace'

type Props = {
  activeInputMode?: AvailableInputModes
  isAgreedPlacePreferenceAvailable: boolean
  isServicePointPreferenceAvailable: boolean
  servicepointKey?: string
  servicepoints: Servicepoint[] | null
  onServicePointInputOpen?: () => void
  onServicePointInputClose?: () => void
  onAgreedPlaceInputOpen?: () => void
  onAgreedPlaceInputClose?: () => void
  onShowServicePointDetails?: () => void
  onNotHomeOpen?: () => void
  onNotHomeClose?: () => void
  onOptionConfigured: () => void
  saveButtonText?: string
}

export const createValidationSchema = (
  t: TFunction
): Yup.ObjectSchema<
  {
    ringDoorbell: object
    servicePoint: object
    agreedPlace: object
  },
  Yup.AnyObject,
  {
    ringDoorbell: object
    servicePoint: object
    agreedPlace: object
  }
> =>
  Yup.object().shape({
    ringDoorbell: Yup.object().when('preference', {
      is: 'ringDoorbell',
      then: (schema) =>
        schema.nullable().test({
          message: t('portal.preferences.ringDoorbell.inputIsRequiredErrorMessage'),
          test: (value, context) => !(context.parent.agreedPlace === null),
        }),
      otherwise: (schema) => schema.nullable(),
    }),
    servicePoint: Yup.object().when('preference', {
      is: 'servicePoint',
      then: (schema) => schema.nullable().required(t('portal.preferences.servicePoint.inputIsRequiredErrorMessage')),
      otherwise: (schema) => schema.nullable(),
    }),
    agreedPlace: Yup.object().when('preference', {
      is: 'agreedPlace',
      then: (schema) => schema.nullable().required(t('portal.preferences.agreedPlace.inputIsRequiredErrorMessage')),
      otherwise: (schema) => schema.nullable(),
    }),
  })

const styles: Record<string, SystemStyleObject<Theme>> = {
  title: {
    paddingBottom: (theme) => theme.spacing(2),
    paddingTop: (theme) => theme.spacing(1),
  },
  paperWrapper: {
    marginTop: (theme) => theme.spacing(1),
    marginBottom: (theme) => theme.spacing(1),
  },
  smallText: {
    fontSize: '0.9rem',
    color: ' #666666',
  },
  faqLink: {
    display: 'flex',
    textDecoration: 'underline',
    color: (theme) => theme.palette.primary.main,
  },
  faqLinkIcon: {
    marginLeft: (theme) => theme.spacing(0.5),
  },
}

export const PreferredSettingsForm: FC<Props> = ({
  activeInputMode,
  isAgreedPlacePreferenceAvailable,
  isServicePointPreferenceAvailable,
  servicepoints,
  servicepointKey,
  onServicePointInputOpen,
  onServicePointInputClose,
  onAgreedPlaceInputOpen,
  onAgreedPlaceInputClose,
  onShowServicePointDetails,
  onNotHomeOpen,
  onNotHomeClose,
  onOptionConfigured,
  saveButtonText,
}) => {
  const { t } = useTranslation()
  const [currentAction, setCurrentAction] = useState<AvailableInputModes | undefined>(activeInputMode)
  const {
    initialValues: { preference: currentPreference },
    setTouched,
    values: { agreedPlace, preference, servicePoint },
    setFieldValue,
  } = useFormikContext<PreferenceFormValues>()
  const previousPreference = useRef(preference)
  const [previousActiveInputMode, setPreviousActiveInputMode] = useState(activeInputMode)

  if (activeInputMode !== previousActiveInputMode) {
    setPreviousActiveInputMode(activeInputMode)
    if (activeInputMode) {
      setCurrentAction(activeInputMode)
    }
  }
  const { handleDialogClosing, handleDialogOpen } = useNativeDialogBackdrop()
  const [scrollEnabled, setScrollEnabled] = useState(true)

  const {
    onSearchTermChange,
    onSearchTermClear,
    searchTerm,
    results: servicepointsToShow,
  } = useServicepointSearch(servicepoints ?? [])

  const onDialogClosing = useCallback(() => {
    handleDialogClosing()
    setScrollEnabled(true)
    setCurrentAction(undefined)
  }, [handleDialogClosing])

  const onDialogOpen = useCallback(() => {
    setScrollEnabled(false)
  }, [])

  const handleSelectServicePoint = async (inputValue: ServicepointInputValue) => {
    onDialogClosing()
    setFieldValue('servicePoint', inputValue)
    onOptionConfigured()
  }

  const handleNotHomeClose = () => {
    onDialogClosing()
    onNotHomeClose?.()
  }

  const handleServicePointInputOpen = () => {
    handleDialogOpen()
    onDialogOpen()
  }

  const handleServicePointInputClose = () => {
    handleDialogClosing()
    onDialogClosing()
    onServicePointInputClose?.()
    onSearchTermClear()
  }

  const handleAgreedPlaceInputClose = () => {
    onDialogClosing()
    onAgreedPlaceInputClose?.()
  }

  const handleSelectAgreedPlace = async (values: AgreedPlaceFormValues) => {
    await setFieldValue('agreedPlace', { ...agreedPlace, ...values, ringDoorbell: false })
    onOptionConfigured()
  }

  const handleSelectChange = async (value: string) => {
    await setFieldValue('preference', value)
  }

  useEffect(() => {
    // Clear any form errors when switching preference
    if (preference !== previousPreference.current) {
      setTouched({}, false)
    }
  }, [preference, setTouched])

  return (
    <Box sx={{ touchAction: scrollEnabled ? 'auto' : 'none' }}>
      <H5>{t('portal.profile.preferencesPage.titleText')}</H5>

      <NotHomeDialog
        open={currentAction === 'not-home'}
        onClose={handleNotHomeClose}
        agreedPlace={agreedPlace}
        isAgreedPlacePreferenceAvailable={isAgreedPlacePreferenceAvailable}
        onChange={onOptionConfigured}
        onOpen={onDialogOpen}
        buttonText={saveButtonText}
      />

      <ServicepointDialog
        showOpeningTimes
        countryCode="NL"
        open={currentAction === 'servicepoint'}
        searchTerm={searchTerm}
        servicepoints={servicepointsToShow}
        showDetailsFor={servicepointKey}
        title={t('portal.preferences.servicePoint.dialogTitle')}
        onChange={handleSelectServicePoint}
        onClose={handleServicePointInputClose}
        onOpen={handleServicePointInputOpen}
        onSearchTermChange={onSearchTermChange}
        onSearchTermClear={onSearchTermClear}
        onShowServicePointDetails={onShowServicePointDetails}
        buttonText={saveButtonText}
      />

      <AgreedPlaceDialog
        agreedPlace={agreedPlace}
        onChange={handleSelectAgreedPlace}
        open={currentAction === 'agreedplace'}
        onClose={handleAgreedPlaceInputClose}
        buttonText={saveButtonText}
      />

      <RadioGroupInput name="preference">
        <Stack direction="column" spacing={2} sx={styles.paperWrapper}>
          <DeliveryOptionRadioInput
            dataTest="check-not-home"
            dataDisplayComponent={<DisplayNotHomeValue agreedPlace={agreedPlace} />}
            description={t('portal.preferences.notHome.subText')}
            configureButtonText={t('portal.preferences.notHome.configureButtonText')}
            editButtonText={t('portal.preferences.notHome.editButtonText')}
            title={t('portal.preferences.notHome.titleText')}
            value="ringDoorbell"
            onConfigureOptionClick={onNotHomeOpen}
            onChange={handleSelectChange}
            isSelected={preference === 'ringDoorbell'}
            isCurrentlyActivePreference={currentPreference === 'ringDoorbell' && agreedPlace !== null}
            hasPreferenceData={agreedPlace !== null && preference === 'ringDoorbell'}
          />

          {isServicePointPreferenceAvailable && servicepoints && servicepoints.length > 0 && (
            <DeliveryOptionRadioInput
              dataDisplayComponent={<DisplayServicePointValue data={servicePoint} />}
              dataTest="check-servicepoint-no-ringdoorbell"
              description={t('portal.preferences.servicePoint.subText')}
              configureButtonText={t('portal.preferences.servicePoint.configureButtonText')}
              editButtonText={t('portal.preferences.servicePoint.editButtonText')}
              title={t('portal.preferences.servicePoint.titleText')}
              value="servicePoint"
              onConfigureOptionClick={onServicePointInputOpen}
              onChange={handleSelectChange}
              isSelected={preference === 'servicePoint'}
              isCurrentlyActivePreference={currentPreference === 'servicePoint'}
              hasPreferenceData={!!servicePoint && preference === 'servicePoint'}
            />
          )}

          {isAgreedPlacePreferenceAvailable && (
            <DeliveryOptionRadioInput
              dataDisplayComponent={<DisplayAgreedPlaceValue data={agreedPlace} />}
              dataTest="check-agreedPlace-no-ringdoorbell"
              description={t('portal.preferences.agreedPlace.subText')}
              configureButtonText={t('portal.preferences.agreedPlace.configureButtonText')}
              editButtonText={t('portal.preferences.agreedPlace.editButtonText')}
              title={t('portal.preferences.agreedPlace.titleText')}
              value="agreedPlace"
              onConfigureOptionClick={onAgreedPlaceInputOpen}
              onChange={handleSelectChange}
              isSelected={preference === 'agreedPlace'}
              isCurrentlyActivePreference={currentPreference === 'agreedPlace'}
              hasPreferenceData={!!agreedPlace?.description && preference === 'agreedPlace'}
            />
          )}
        </Stack>
      </RadioGroupInput>
    </Box>
  )
}
